import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import formatWattage from "../Common/formatWattage";
import { buildMatDisplayEntries } from "../Common/matLayoutColors";
import {
  getTotalMatOutput,
  requiresContactor,
} from "../Common/matElectricalLoad";
import { isHeatMatApp } from "../Common/heatMatAppBridge";

function DetailsPopup({ setDetailsPopup, store, onNativePdf }) {
  const { t } = useTranslation();
  const listRef = useRef(null);
  const matDisplayEntries = buildMatDisplayEntries(store.mat_data);
  const totalOutput = getTotalMatOutput(store.mat_data);
  const exceedsThermostatLoad = requiresContactor(store.mat_data);
  const runningInHeatMatApp = isHeatMatApp();

  const handlePrintList = () => {
    if (!listRef.current) return;

    const printContent = listRef.current.innerHTML;
    const printWindow = window.open("", "", "height=900,width=800");
    if (!printWindow) return;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>${t("List of parts")}</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; margin: 40px; color: #171717; }
            .header { text-align: center; margin-bottom: 30px; }
            .header img { width: 180px; margin-bottom: 10px; }
            h1 { font-size: 22px; margin: 10px 0 0; font-weight: 600; }
            ul { list-style: none; padding: 0; margin: 0; }
            li { margin: 0 0 12px; padding: 16px; border: 1px solid #ddd; border-radius: 8px; }
            .row { display: grid; grid-template-columns: 12px minmax(0, 1fr) 130px 130px; align-items: center; gap: 12px; }
            .dot { width: 9px; height: 9px; border-radius: 50%; background: #ff970d; }
            .result-details-item__product, .result-details-item__metric { display: flex; flex-direction: column; gap: 4px; }
            .result-details-item__product span, .result-details-item__metric span { color: #555; font-size: 12px; }
            .result-details-item__metric { border-left: 1px solid #ddd; padding-left: 20px; }
            .result-details-item--thermostat .row { grid-template-columns: 12px minmax(0, 1fr); }
            .result-details-load-summary { border-left: 4px solid #ff970d; }
            .result-details-load-summary__total { display: flex; justify-content: space-between; gap: 20px; font-size: 14px; }
            .result-details-load-summary p { margin: 10px 0 0; padding-top: 10px; border-top: 1px solid #e4c4c0; color: #8e2f26; font-size: 12px; line-height: 1.5; }
            .result-details-load-summary--warning { border-left-color: #c94738; background: #fff6f4; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="${window.location.origin}/img/logo_main.png" alt="Heat Mat" />
            <h1>${t("List of parts")}</h1>
          </div>
          <ul>${printContent}</ul>
          <hr style="margin: 40px 0; border: 0; border-top: 1px solid #ddd;" />
          <div style="font-size: 13px; color: #444; text-align: center; line-height: 1.6;">
            <strong>Tel:</strong>
            <a href="tel:01444247020" style="color: #f89f37; text-decoration: none;">01444 247020</a><br />
            <strong>Email:</strong>
            <a href="mailto:sales@heatmat.co.uk" style="color: #f89f37; text-decoration: none;">sales@heatmat.co.uk</a>
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  return (
    <div
      className="result-details-overlay"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setDetailsPopup(0);
      }}
    >
      <div
        className="result-details-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="parts-list-title"
      >
        <button
          type="button"
          className="result-details-close"
          aria-label={t("Close")}
          onClick={() => setDetailsPopup(0)}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6 6 18" />
          </svg>
        </button>

        <header className="result-details-header">
          <h2 id="parts-list-title">{t("List of parts")}</h2>
          <p>{t("Learn more")}</p>
        </header>

        <div className="result-details-scroll">
          <ul ref={listRef} className="result-details-list">
            {matDisplayEntries.map(({ mat, color, key }) => (
              <li key={key} className="result-details-item">
                <div className="result-details-item__row row">
                  <span
                    className="result-details-item__dot dot"
                    style={{ backgroundColor: color }}
                  />
                  <div className="result-details-item__product">
                    <strong>1 x {mat.MatId}</strong>
                    <span>{store.mat_data.chosenMatDescription.type}</span>
                  </div>
                  <div className="result-details-item__metric">
                    <span>{t("Coverage")}</span>
                    <strong>{mat.Size} m²</strong>
                  </div>
                  <div className="result-details-item__metric">
                    <span>{t("Output")}</span>
                    <strong>{formatWattage(mat.Wattage)}W</strong>
                  </div>
                </div>
              </li>
            ))}

            {(matDisplayEntries.length > 1 || exceedsThermostatLoad) && (
              <li
                className={`result-details-item result-details-load-summary${
                  exceedsThermostatLoad
                    ? " result-details-load-summary--warning"
                    : ""
                }`}
              >
                <div className="result-details-load-summary__total">
                  <span>{t("Total connected output")}</span>
                  <strong>{formatWattage(totalOutput)}W</strong>
                </div>
                {exceedsThermostatLoad && (
                  <p>{t("thermostat_load_warning")}</p>
                )}
              </li>
            )}

            <li className="result-details-item result-details-item--thermostat">
              <div className="result-details-item__row row">
                <span className="result-details-item__dot dot" />
                <div className="result-details-item__product">
                  <strong>
                    1 x {store.thermoInfo ? store.thermoInfo.name : "thermostat"}
                  </strong>
                  <span>
                    {store.thermoInfo
                      ? store.thermoInfo.description || "thermostat"
                      : "thermostat"}
                  </span>
                </div>
              </div>
            </li>
          </ul>
        </div>

        <footer
          className={`result-details-actions${
            runningInHeatMatApp ? " result-details-actions--app" : ""
          }`}
        >
          {!runningInHeatMatApp && (
            <button
              type="button"
              className="result-details-action result-details-action--print"
              onClick={handlePrintList}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 9V4h10v5M7 17H5a2 2 0 0 1-2-2v-4a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v4a2 2 0 0 1-2 2h-2M7 14h10v6H7z" />
              </svg>
              {t("Print this list")}
            </button>
          )}

          {runningInHeatMatApp ? (
            <button
              type="button"
              onClick={onNativePdf}
              className="result-details-action result-details-action--pdf"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 2h8l4 4v16H6zM14 2v5h5M9 15h6M9 18h4" />
              </svg>
              {t("Save PDF")}
            </button>
          ) : (
            <Link
              to="/pdf-page"
              target="_blank"
              className="result-details-action result-details-action--pdf"
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M6 2h8l4 4v16H6zM14 2v5h5M9 15h6M9 18h4" />
              </svg>
              {t("Save PDF")}
            </Link>
          )}
        </footer>
      </div>
    </div>
  );
}

export default DetailsPopup;
